/**
 * Entity Label Registry
 *
 * Each UI plugin registers its own resolvers that turn an internal entityId
 * into a human-readable reference (e.g. "purchaseOrder/abc-123" → "PO-2026-00014").
 * Used by the chat thread list so users see "PO-2026-00014" instead of a
 * truncated internal id.
 *
 * Mirrors the shape of `entity-route-for.ts` — same plugin-owned-registry
 * pattern, different resolver signature (async + returns a label string).
 */

export type EntityLabelResolver = (entityId: string) => Promise<string | null>;

const REGISTRY = new Map<string, EntityLabelResolver>();

const keyOf = (pluginId: string, entityType: string): string =>
  `${pluginId}:${entityType}`;

export const registerEntityLabel = (
  pluginId: string,
  entityType: string,
  resolver: EntityLabelResolver,
): void => {
  REGISTRY.set(keyOf(pluginId, entityType), resolver);
};

export const registerEntityLabels = (
  pluginId: string,
  resolvers: Record<string, EntityLabelResolver>,
): void => {
  for (const [entityType, fn] of Object.entries(resolvers)) {
    registerEntityLabel(pluginId, entityType, fn);
  }
};

export interface EntityLabelInput {
  pluginId?: string;
  entityType?: string;
  entityId?: string;
}

export const entityLabelResolverFor = (
  scope: EntityLabelInput,
): EntityLabelResolver | null => {
  if (!scope.pluginId || !scope.entityType) return null;
  return REGISTRY.get(keyOf(scope.pluginId, scope.entityType)) ?? null;
};

/** Test-only: clear the registry between test runs. */
export const __resetEntityLabels = (): void => {
  REGISTRY.clear();
};
